/**
 * Permission Registry — Single Source of Truth
 *
 * Aligned with Fernando's Roles & Permissions Matrix v3 CSV (2026-05).
 * Permission registry synced from the matrix plus product-specific extensions; 12 default roles.
 *
 * Imported by:
 * - Backend guards (`@RequirePermission()`)
 * - Frontend hooks (`useCan()`)
 * - Frontend components (`<Can>`)
 * - Sidebar navigation filtering
 * - Route-level access control
 * - WorkOS setup/reconciliation scripts (`packages/auth/scripts/setup-workos.mts`)
 *
 * Permission key format: `category:resource:action` or `category:resource:sub:action`.
 *
 * Display labels are NOT stored here — they live in the web i18n `permissions` namespace
 * (`apps/web/src/lib/i18n.ts`). The registry only carries stable structural keys
 * (`category`, `displayGroupKey`, `displayItemKey`).
 */

/**
 * Permission definition metadata.
 *
 * - `resource` — dot-separated resource path (matches WorkOS slug minus action).
 * - `action` — verb (read, create, update, edit, delete, manage, submit, reclassify, request, send, approve).
 * - `category` — module key for grouping (`ap`, `gl`, `ar`, `identity`, etc.).
 * - `description` — short English description (admin UI, code review, audit). Never user-visible.
 * - `displayGroupKey` — i18n key under `permissions.group.*` (e.g., `ap.bill`).
 * - `displayItemKey` — i18n key under `permissions.item.*` (typically equals `action`).
 */
export interface PermissionDefinition {
  readonly resource: string;
  readonly action: string;
  readonly category: string;
  readonly description: string;
  readonly displayGroupKey: string;
  readonly displayItemKey: string;
}
/**
 * Complete permission registry.
 *
 * Source of truth: Fernando's Roles & Permissions Matrix v3 (63 perms) +
 * `hcm:manage` (kept for HCM module management beyond payroll runs).
 */
export const PERMISSIONS = {
  // ── GL — Contabilidade ──
  'gl:read': {
    resource: 'gl',
    action: 'read',
    category: 'gl',
    description:
      'View all GL data (accounts, journals, ledger, balances, books, calendars, periods, rollups, managerial accounts)',
    displayGroupKey: 'gl',
    displayItemKey: 'read',
  },
  'gl:manage': {
    resource: 'gl',
    action: 'manage',
    category: 'gl',
    description:
      'Manage GL configuration (books, calendars, periods, account mappings, rollups, managerial accounts)',
    displayGroupKey: 'gl',
    displayItemKey: 'manage',
  },
  // Manual origin linking (ADR 0011). Deliberately not `gl:manage`: this is granted to
  // accountants and super-admins for onboarding a tenant's historical data, and must be
  // grantable and revocable without also granting control of the chart of accounts,
  // periods or journals.
  'gl:origin-link:read': {
    resource: 'gl.origin-link',
    action: 'read',
    category: 'gl',
    description:
      'View the origin-linking console: imported journals, unlinked records, ranked candidates and the link audit trail',
    displayGroupKey: 'gl.origin-link',
    displayItemKey: 'read',
  },
  'gl:origin-link:manage': {
    resource: 'gl.origin-link',
    action: 'manage',
    category: 'gl',
    description:
      'Link and unlink historically-imported journals to bills, payments, invoices and cash movements (writes no GL data)',
    displayGroupKey: 'gl.origin-link',
    displayItemKey: 'manage',
  },
  'gl:account:create': {
    resource: 'gl.account',
    action: 'create',
    category: 'gl',
    description: 'Create GL accounts',
    displayGroupKey: 'gl.account',
    displayItemKey: 'create',
  },
  'gl:account:update': {
    resource: 'gl.account',
    action: 'update',
    category: 'gl',
    description: 'Edit GL accounts',
    displayGroupKey: 'gl.account',
    displayItemKey: 'update',
  },
  'gl:closing:manage': {
    resource: 'gl.closing',
    action: 'manage',
    category: 'gl',
    description: 'Manage period closing process',
    displayGroupKey: 'gl.closing',
    displayItemKey: 'manage',
  },
  'gl:closing:task:manage': {
    resource: 'gl.closing.task',
    action: 'manage',
    category: 'gl',
    description: 'Manage custom closing checklist tasks',
    displayGroupKey: 'gl.closing.task',
    displayItemKey: 'manage',
  },
  'gl:fx-recovery:request': {
    resource: 'gl.fx-recovery',
    action: 'request',
    category: 'gl',
    description: 'Create an evidenced historical FX recovery preview',
    displayGroupKey: 'gl.fx-recovery',
    displayItemKey: 'request',
  },
  'gl:fx-recovery:approve': {
    resource: 'gl.fx-recovery',
    action: 'approve',
    category: 'gl',
    description: 'Approve or cancel a historical FX recovery',
    displayGroupKey: 'gl.fx-recovery',
    displayItemKey: 'approve',
  },
  'gl:fx-recovery:execute': {
    resource: 'gl.fx-recovery',
    action: 'execute',
    category: 'gl',
    description: 'Execute an approved historical FX recovery and its journal reversals',
    displayGroupKey: 'gl.fx-recovery',
    displayItemKey: 'execute',
  },
  'gl:reversal:request': {
    resource: 'gl.reversal',
    action: 'request',
    category: 'gl',
    description: 'Request a GL reversal (enters workflow)',
    displayGroupKey: 'gl.reversal',
    displayItemKey: 'request',
  },
  'gl:reversal:create': {
    resource: 'gl.reversal',
    action: 'create',
    category: 'gl',
    description: 'Execute a GL reversal directly (no workflow)',
    displayGroupKey: 'gl.reversal',
    displayItemKey: 'create',
  },
  'gl:journal:draft': {
    resource: 'gl.journal',
    action: 'draft',
    category: 'gl',
    description: 'Create, update, and cancel draft manual journal entries',
    displayGroupKey: 'gl.journal',
    displayItemKey: 'draft',
  },
  'gl:journal:post': {
    resource: 'gl.journal',
    action: 'post',
    category: 'gl',
    description: 'Post draft manual journal entries to the ledger',
    displayGroupKey: 'gl.journal',
    displayItemKey: 'post',
  },
  'gl:revenue-recognition:manage': {
    resource: 'gl.revenue-recognition',
    action: 'manage',
    category: 'gl',
    description: 'Generate and manage contract revenue-recognition schedules',
    displayGroupKey: 'gl.revenue-recognition',
    displayItemKey: 'manage',
  },

  // ── AP — Contas a Pagar ──
  'ap:expense-accrual:read': {
    resource: 'ap.expense-accrual',
    action: 'read',
    category: 'ap',
    description: 'View expense accrual proposals, posted accruals, and settlement suggestions',
    displayGroupKey: 'ap.expense-accrual',
    displayItemKey: 'read',
  },
  'ap:expense-accrual:propose': {
    resource: 'ap.expense-accrual',
    action: 'propose',
    category: 'ap',
    description: 'Create, propose, and dismiss expense accruals without posting to the ledger',
    displayGroupKey: 'ap.expense-accrual',
    displayItemKey: 'propose',
  },
  'ap:expense-accrual:confirm': {
    resource: 'ap.expense-accrual',
    action: 'confirm',
    category: 'ap',
    description:
      'Confirm, settle, keep, or reverse expense accruals, and configure the liability account',
    displayGroupKey: 'ap.expense-accrual',
    displayItemKey: 'confirm',
  },
  'ap:bill:read': {
    resource: 'ap.bill',
    action: 'read',
    category: 'ap',
    description: 'View AP bills and details',
    displayGroupKey: 'ap.bill',
    displayItemKey: 'read',
  },
  'ap:bill:create': {
    resource: 'ap.bill',
    action: 'create',
    category: 'ap',
    description: 'Create AP bills (manual + import)',
    displayGroupKey: 'ap.bill',
    displayItemKey: 'create',
  },
  'ap:bill:update': {
    resource: 'ap.bill',
    action: 'update',
    category: 'ap',
    description: 'Edit AP bills in DRAFT/SUBMITTED state',
    displayGroupKey: 'ap.bill',
    displayItemKey: 'update',
  },
  'ap:statutory-treatment:manage': {
    resource: 'ap.statutory-treatment',
    action: 'manage',
    category: 'ap',
    description:
      'Change statutory tax-slot treatments and mapped liability accounts from bill review',
    displayGroupKey: 'ap.statutory-treatment',
    displayItemKey: 'manage',
  },
  'ap:bill:delete': {
    resource: 'ap.bill',
    action: 'delete',
    category: 'ap',
    description: 'Delete/void AP bills',
    displayGroupKey: 'ap.bill',
    displayItemKey: 'delete',
  },
  'ap:bill:submit': {
    resource: 'ap.bill',
    action: 'submit',
    category: 'ap',
    description: 'Submit AP bill for approval',
    displayGroupKey: 'ap.bill',
    displayItemKey: 'submit',
  },
  'ap:bill:reclassify': {
    resource: 'ap.bill',
    action: 'reclassify',
    category: 'ap',
    description: 'Change dimensions on APPROVED/PAID bills',
    displayGroupKey: 'ap.bill',
    displayItemKey: 'reclassify',
  },
  'ap:payment:read': {
    resource: 'ap.payment',
    action: 'read',
    category: 'ap',
    description: 'View AP payments',
    displayGroupKey: 'ap.payment',
    displayItemKey: 'read',
  },
  'ap:payment:create': {
    resource: 'ap.payment',
    action: 'create',
    category: 'ap',
    description: 'Create/schedule AP payments',
    displayGroupKey: 'ap.payment',
    displayItemKey: 'create',
  },
  'ap:payment:update': {
    resource: 'ap.payment',
    action: 'update',
    category: 'ap',
    description: 'Edit payment details',
    displayGroupKey: 'ap.payment',
    displayItemKey: 'update',
  },
  'ap:payment:delete': {
    resource: 'ap.payment',
    action: 'delete',
    category: 'ap',
    description: 'Delete/cancel payments',
    displayGroupKey: 'ap.payment',
    displayItemKey: 'delete',
  },
  'ap:payment:send': {
    resource: 'ap.payment',
    action: 'send',
    category: 'ap',
    description: 'Dispatch payments to bank',
    displayGroupKey: 'ap.payment',
    displayItemKey: 'send',
  },
  'ap:document:read': {
    resource: 'ap.document',
    action: 'read',
    category: 'ap',
    description: 'View document inbox',
    displayGroupKey: 'ap.document',
    displayItemKey: 'read',
  },
  'ap:received-nf:read': {
    resource: 'ap.received-nf',
    action: 'read',
    category: 'ap',
    description: 'View received NFs',
    displayGroupKey: 'ap.received-nf',
    displayItemKey: 'read',
  },
  'ap:reimbursement:read': {
    resource: 'ap.reimbursement',
    action: 'read',
    category: 'ap',
    description: 'View reimbursement requests',
    displayGroupKey: 'ap.reimbursement',
    displayItemKey: 'read',
  },
  'ap:reimbursement:all:read': {
    resource: 'ap.reimbursement.all',
    action: 'read',
    category: 'ap',
    description: 'View all reimbursement requests within tenant and entity scope',
    displayGroupKey: 'ap.reimbursement',
    displayItemKey: 'readAll',
  },
  'ap:reimbursement:managed:read': {
    resource: 'ap.reimbursement.managed',
    action: 'read',
    category: 'ap',
    description: 'View reimbursement requests in departments and cost centers managed by the user',
    displayGroupKey: 'ap.reimbursement',
    displayItemKey: 'readManaged',
  },
  'ap:reimbursement:own:read': {
    resource: 'ap.reimbursement.own',
    action: 'read',
    category: 'ap',
    description: 'View reimbursement requests created or submitted by the user',
    displayGroupKey: 'ap.reimbursement',
    displayItemKey: 'readOwn',
  },
  'ap:reimbursement:create': {
    resource: 'ap.reimbursement',
    action: 'create',
    category: 'ap',
    description: 'Create reimbursement requests',
    displayGroupKey: 'ap.reimbursement',
    displayItemKey: 'create',
  },
  'ap:reimbursement:manage': {
    resource: 'ap.reimbursement',
    action: 'manage',
    category: 'ap',
    description: 'Manage reimbursement policies',
    displayGroupKey: 'ap.reimbursement',
    displayItemKey: 'manage',
  },
  'ap:contract-review:read': {
    resource: 'ap.contract-review',
    action: 'read',
    category: 'ap',
    description: 'View procurement contract review requests, flags, and settings',
    displayGroupKey: 'ap.contract-review',
    displayItemKey: 'read',
  },
  'ap:contract-review:manage': {
    resource: 'ap.contract-review',
    action: 'manage',
    category: 'ap',
    description:
      'Create and triage contract reviews, edit prompts/settings, and run admin recovery actions',
    displayGroupKey: 'ap.contract-review',
    displayItemKey: 'manage',
  },
  'ap:onfly-auto-approval:manage': {
    resource: 'ap.onfly-auto-approval',
    action: 'manage',
    category: 'ap',
    description:
      'Enable or revoke automatic approval and posting of OnFly travel bills, waiving human review',
    displayGroupKey: 'ap.onfly-auto-approval',
    displayItemKey: 'manage',
  },
  'ap:vendor-knowledge:manage': {
    resource: 'ap.vendor-knowledge',
    action: 'manage',
    category: 'ap',
    description:
      'Create, version, deactivate, dismiss and restore vendor knowledge (withholding and PIS/COFINS credit rules, instructions)',
    displayGroupKey: 'ap.vendor-knowledge',
    displayItemKey: 'manage',
  },
  'ap:contract-review:legal-approve': {
    resource: 'ap.contract-review',
    action: 'legal-approve',
    category: 'ap',
    description: 'Approve or reject legal approval stages assigned to the user',
    displayGroupKey: 'ap.contract-review',
    displayItemKey: 'legalApprove',
  },
  'ap:contract-review:risk-approve': {
    resource: 'ap.contract-review',
    action: 'risk-approve',
    category: 'ap',
    description: 'Approve or reject business risk approval stages assigned to the user',
    displayGroupKey: 'ap.contract-review',
    displayItemKey: 'riskApprove',
  },
  'ap:contract-review:approval-override': {
    resource: 'ap.contract-review',
    action: 'approval-override',
    category: 'ap',
    description: 'Override contract review approval stage assignments when needed',
    displayGroupKey: 'ap.contract-review',
    displayItemKey: 'approvalOverride',
  },
  'ap:contract-review:settings-manage': {
    resource: 'ap.contract-review',
    action: 'settings-manage',
    category: 'ap',
    description: 'Manage contract review prompts, agent catalog, and module settings',
    displayGroupKey: 'ap.contract-review',
    displayItemKey: 'settingsManage',
  },
  'ap:purchase-request:read': {
    resource: 'ap.purchase-request',
    action: 'read',
    category: 'ap',
    description: 'View purchase requests and approval status',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'read',
  },
  'ap:purchase-request:create': {
    resource: 'ap.purchase-request',
    action: 'create',
    category: 'ap',
    description: 'Create purchase requests',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'create',
  },
  'ap:purchase-request:manage': {
    resource: 'ap.purchase-request',
    action: 'manage',
    category: 'ap',
    description:
      'Manage purchase requests, including edits, cancellation, and document attachments',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'manage',
  },
  'ap:purchase-request:update': {
    resource: 'ap.purchase-request',
    action: 'update',
    category: 'ap',
    description: 'Edit draft purchase requests',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'update',
  },
  'ap:purchase-request:submit': {
    resource: 'ap.purchase-request',
    action: 'submit',
    category: 'ap',
    description: 'Submit purchase requests for approval',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'submit',
  },
  'ap:purchase-request:approve': {
    resource: 'ap.purchase-request',
    action: 'approve',
    category: 'ap',
    description: 'Approve purchase requests and convert them to purchase orders',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'approve',
  },
  'ap:purchase-request:reject': {
    resource: 'ap.purchase-request',
    action: 'reject',
    category: 'ap',
    description: 'Reject purchase requests in approval workflow',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'reject',
  },
  'ap:purchase-request:cancel': {
    resource: 'ap.purchase-request',
    action: 'cancel',
    category: 'ap',
    description: 'Cancel draft or approved purchase requests',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'cancel',
  },
  'ap:purchase-order:read': {
    resource: 'ap.purchase-order',
    action: 'read',
    category: 'ap',
    description: 'View purchase orders and commitment sync status',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'read',
  },
  'ap:purchase-order:create': {
    resource: 'ap.purchase-order',
    action: 'create',
    category: 'ap',
    description: 'Create purchase orders directly',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'create',
  },
  'ap:purchase-order:manage': {
    resource: 'ap.purchase-order',
    action: 'manage',
    category: 'ap',
    description: 'Update purchase orders and upload supporting documents',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'manage',
  },
  'ap:purchase-order:mark-ready': {
    resource: 'ap.purchase-order',
    action: 'mark-ready',
    category: 'ap',
    description: 'Mark purchase orders ready with vendor, terms, and final amount',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'mark-ready',
  },
  'ap:purchase-order:fulfill': {
    resource: 'ap.purchase-order',
    action: 'fulfill',
    category: 'ap',
    description: 'Mark purchase orders as fulfilled after billing',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'fulfill',
  },
  'ap:purchase-order:receive': {
    resource: 'ap.purchase-order',
    action: 'receive',
    category: 'ap',
    description: 'Record goods or services receipt for three-way matching',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'receive',
  },
  'ap:purchase-order:link-bill': {
    resource: 'ap.purchase-order',
    action: 'link-bill',
    category: 'ap',
    description: 'Link AP bills to purchase orders for matching',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'link-bill',
  },
  'ap:purchase-request:convert': {
    resource: 'ap.purchase-request',
    action: 'convert',
    category: 'ap',
    description: 'Convert approved purchase requests into purchase orders',
    displayGroupKey: 'ap.purchase-request',
    displayItemKey: 'convert',
  },
  'ap:purchase-order:cancel': {
    resource: 'ap.purchase-order',
    action: 'cancel',
    category: 'ap',
    description: 'Cancel purchase orders and associated commitments',
    displayGroupKey: 'ap.purchase-order',
    displayItemKey: 'cancel',
  },

  // ── Budget — Orçamento ──
  'budget:read': {
    resource: 'budget',
    action: 'read',
    category: 'budget',
    description: 'View budget plans (list and detail)',
    displayGroupKey: 'budget',
    displayItemKey: 'read',
  },
  'budget:manage': {
    resource: 'budget',
    action: 'manage',
    category: 'budget',
    description: 'Create, duplicate, activate, archive, and edit budget plans',
    displayGroupKey: 'budget',
    displayItemKey: 'manage',
  },
  'budget:line:read': {
    resource: 'budget.line',
    action: 'read',
    category: 'budget',
    description: 'View budget lines, alerts, and utilization',
    displayGroupKey: 'budget.line',
    displayItemKey: 'read',
  },
  'budget:line:manage': {
    resource: 'budget.line',
    action: 'manage',
    category: 'budget',
    description: 'Create, edit, archive, and configure budget lines',
    displayGroupKey: 'budget.line',
    displayItemKey: 'manage',
  },
  'budget:exception:read': {
    resource: 'budget.exception',
    action: 'read',
    category: 'budget',
    description: 'View budget exception requests and review queue',
    displayGroupKey: 'budget.exception',
    displayItemKey: 'read',
  },

  // ── FP&A — Relatórios Gerenciais ──
  'fpa:read': {
    resource: 'fpa',
    action: 'read',
    category: 'fpa',
    description: 'View FP&A rollups, templates, metrics, and evaluated reports',
    displayGroupKey: 'fpa',
    displayItemKey: 'read',
  },
  'fpa:manage': {
    resource: 'fpa',
    action: 'manage',
    category: 'fpa',
    description: 'Create, edit, and archive FP&A rollups, templates, and metrics',
    displayGroupKey: 'fpa',
    displayItemKey: 'manage',
  },

  // ── AR — Contas a Receber ──
  'ar:read': {
    resource: 'ar',
    action: 'read',
    category: 'ar',
    description: 'View all AR data (invoices, receipts, pre-billing, services, groups, customers)',
    displayGroupKey: 'ar',
    displayItemKey: 'read',
  },
  'ar:manage': {
    resource: 'ar',
    action: 'manage',
    category: 'ar',
    description: 'Manage all AR data',
    displayGroupKey: 'ar',
    displayItemKey: 'manage',
  },
  'ar:create': {
    resource: 'ar',
    action: 'create',
    category: 'ar',
    description: 'Create AR entities (invoices, customers, services, etc.)',
    displayGroupKey: 'ar',
    displayItemKey: 'create',
  },
  'ar:approve': {
    resource: 'ar',
    action: 'approve',
    category: 'ar',
    description: 'Approve AR invoices by issuing them or posting them to GL',
    displayGroupKey: 'ar',
    displayItemKey: 'approve',
  },
  'ar:update': {
    resource: 'ar',
    action: 'update',
    category: 'ar',
    description: 'Edit AR entities',
    displayGroupKey: 'ar',
    displayItemKey: 'update',
  },
  'ar:delete': {
    resource: 'ar',
    action: 'delete',
    category: 'ar',
    description: 'Delete AR entities',
    displayGroupKey: 'ar',
    displayItemKey: 'delete',
  },
  'ar:service:read': {
    resource: 'ar.service',
    action: 'read',
    category: 'ar',
    description: 'View billing services and cost services, with their details and links',
    displayGroupKey: 'ar.service',
    displayItemKey: 'read',
  },
  'ar:service:create': {
    resource: 'ar.service',
    action: 'create',
    category: 'ar',
    description: 'Create billing services and cost services',
    displayGroupKey: 'ar.service',
    displayItemKey: 'create',
  },
  'ar:service:edit': {
    resource: 'ar.service',
    action: 'edit',
    category: 'ar',
    description:
      'Edit billing services and cost services, including activating, deactivating and linking them',
    displayGroupKey: 'ar.service',
    displayItemKey: 'edit',
  },
  'ar:service:manage': {
    resource: 'ar.service',
    action: 'manage',
    category: 'ar',
    description: 'Manage billing service catalog items, including deletion',
    displayGroupKey: 'ar.service',
    displayItemKey: 'manage',
  },
  'ar:invoice:classify': {
    resource: 'ar.invoice',
    action: 'classify',
    category: 'ar',
    description: 'Save AR invoice classifications before GL posting',
    displayGroupKey: 'ar',
    displayItemKey: 'invoiceClassify',
  },
  'ar:invoice:reclassify': {
    resource: 'ar.invoice',
    action: 'reclassify',
    category: 'ar',
    description: 'Reclassify posted AR invoices (master approver flow)',
    displayGroupKey: 'ar',
    displayItemKey: 'invoiceReclassify',
  },
  'ar:invoice:cancel-fiscal': {
    resource: 'ar.invoice',
    action: 'cancel-fiscal',
    category: 'ar',
    description: 'Cancel emitted NFSe and reverse AR invoice GL postings',
    displayGroupKey: 'ar',
    displayItemKey: 'invoiceCancelFiscal',
  },
  'ar:contract:read': {
    resource: 'ar.contract',
    action: 'read',
    category: 'ar',
    description: 'View billing contracts, versions, and items',
    displayGroupKey: 'ar',
    displayItemKey: 'contractRead',
  },
  'ar:contract:manage': {
    resource: 'ar.contract',
    action: 'manage',
    category: 'ar',
    description:
      'Create, activate, suspend, reactivate, and cancel billing contracts, including their billing milestones',
    displayGroupKey: 'ar',
    displayItemKey: 'contractManage',
  },
  'ar:contract:approve': {
    resource: 'ar.contract',
    action: 'approve',
    category: 'ar',
    description:
      'Approve or reject billing contract amendments (upsell, downgrade, suspension, cancellation)',
    displayGroupKey: 'ar',
    displayItemKey: 'contractApprove',
  },
  'ar:contract:pricing': {
    resource: 'ar.contract',
    action: 'pricing',
    category: 'ar',
    description:
      'Commit commercial prices to a Contract Item at activation or amendment approval, and approve below-floor pricing exceptions',
    displayGroupKey: 'ar',
    displayItemKey: 'contractPricing',
  },
  'ar:usage:read': {
    resource: 'ar.usage',
    action: 'read',
    category: 'ar',
    description: 'View usage records ingested for ARREARS-billed contract items',
    displayGroupKey: 'ar',
    displayItemKey: 'usageRead',
  },
  'ar:usage:manage': {
    resource: 'ar.usage',
    action: 'manage',
    category: 'ar',
    description: 'Ingest and finalize usage records (manual, CSV, and API), including corrections',
    displayGroupKey: 'ar',
    displayItemKey: 'usageManage',
  },
  'ar:debit-note:read': {
    resource: 'ar.debit-note',
    action: 'read',
    category: 'ar',
    description: 'View AR debit notes for client-reimbursable expenses',
    displayGroupKey: 'ar',
    displayItemKey: 'debitNoteRead',
  },
  'ar:debit-note:manage': {
    resource: 'ar.debit-note',
    action: 'manage',
    category: 'ar',
    description: 'Create, issue, void, and settle AR debit notes',
    displayGroupKey: 'ar',
    displayItemKey: 'debitNoteManage',
  },

  // ── Party — Contrapartes ──
  'party:read': {
    resource: 'party',
    action: 'read',
    category: 'party',
    description: 'View vendors and customers',
    displayGroupKey: 'party',
    displayItemKey: 'read',
  },
  'party:manage': {
    resource: 'party',
    action: 'manage',
    category: 'party',
    description: 'Create/edit vendors and customers',
    displayGroupKey: 'party',
    displayItemKey: 'manage',
  },
  'party:create': {
    resource: 'party',
    action: 'create',
    category: 'party',
    description: 'Create vendors and customers',
    displayGroupKey: 'party',
    displayItemKey: 'create',
  },
  'party:update': {
    resource: 'party',
    action: 'update',
    category: 'party',
    description: 'Edit vendors and customers',
    displayGroupKey: 'party',
    displayItemKey: 'update',
  },
  'party:delete': {
    resource: 'party',
    action: 'delete',
    category: 'party',
    description: 'Delete vendors and customers',
    displayGroupKey: 'party',
    displayItemKey: 'delete',
  },
  'party:bank-info:approve': {
    resource: 'party.bank-info',
    action: 'approve',
    category: 'party',
    description: 'Approve vendor/customer bank information changes',
    displayGroupKey: 'party.bank-info',
    displayItemKey: 'approve',
  },
  'party:vendor-bank-account:manage': {
    resource: 'party.vendor-bank-account',
    action: 'manage',
    category: 'party',
    description: 'Create/edit vendor bank accounts (financial control)',
    displayGroupKey: 'party.vendor-bank-account',
    displayItemKey: 'manage',
  },

  // ── HCM — Pessoas ──
  'hcm:read': {
    resource: 'hcm',
    action: 'read',
    category: 'hcm',
    description: 'View employees and payroll',
    displayGroupKey: 'hcm',
    displayItemKey: 'read',
  },
  'hcm:manage': {
    resource: 'hcm',
    action: 'manage',
    category: 'hcm',
    description: 'Manage HCM configuration and operations',
    displayGroupKey: 'hcm',
    displayItemKey: 'manage',
  },
  'hcm:payroll:create': {
    resource: 'hcm.payroll',
    action: 'create',
    category: 'hcm',
    description: 'Generate/import payroll (creates draft for review)',
    displayGroupKey: 'hcm.payroll',
    displayItemKey: 'create',
  },
  'hcm:payroll:approve': {
    resource: 'hcm.payroll',
    action: 'approve',
    category: 'hcm',
    description: 'Approve payroll (generates AP bills + GL postings)',
    displayGroupKey: 'hcm.payroll',
    displayItemKey: 'approve',
  },

  // ── OP — Tesouraria ──
  'op:read': {
    resource: 'op',
    action: 'read',
    category: 'op',
    description: 'View bank accounts, cards, investments, reconciliation',
    displayGroupKey: 'op',
    displayItemKey: 'read',
  },
  'op:manage': {
    resource: 'op',
    action: 'manage',
    category: 'op',
    description: 'Manage treasury and reconciliation',
    displayGroupKey: 'op',
    displayItemKey: 'manage',
  },
  /**
   * Closing one bank account's statement for one month (ALC-4242). Separate from `op:manage`
   * because closing is an accounting act, not a treasury edit: it freezes the month's bank
   * side and every later write into it has to go through a reopen. Granted to the roles that
   * hold `op:manage` today, so no role loses an ability it already had.
   */
  'reconciliation:close': {
    resource: 'reconciliation',
    action: 'close',
    category: 'op',
    description: 'Close a bank account statement for a month once the balance proof is clean',
    displayGroupKey: 'op',
    displayItemKey: 'close',
  },
  /**
   * Reopening a closed month. Always justified, always audited.
   *
   * Deliberately narrower than `reconciliation:close` (decision D-S6d): closing is the
   * routine end of a treasury month, while reopening unlocks numbers that were already
   * reported. It is granted to the closing authorities — admin, super-admin, CFO and
   * controller — and not to every role that may close.
   */
  'reconciliation:reopen': {
    resource: 'reconciliation',
    action: 'reopen',
    category: 'op',
    description: 'Reopen a closed bank account statement with a recorded justification',
    displayGroupKey: 'op',
    displayItemKey: 'reopen',
  },
  'op:credit-card:all:read': {
    resource: 'op.credit-card.all',
    action: 'read',
    category: 'op',
    description: 'View all credit cards and card dashboards within tenant and entity scope',
    displayGroupKey: 'op.credit-card',
    displayItemKey: 'readAll',
  },
  'op:credit-card:managed:read': {
    resource: 'op.credit-card.managed',
    action: 'read',
    category: 'op',
    description:
      'View credit cards and card dashboards in departments and cost centers managed by the user',
    displayGroupKey: 'op.credit-card',
    displayItemKey: 'readManaged',
  },

  // ── Fine-grained authorization ──
  'fga:manage': {
    resource: 'fga',
    action: 'manage',
    category: 'identity',
    description: 'Manage person-level access policies for protected resources',
    displayGroupKey: 'identity.authorization',
    displayItemKey: 'manage',
  },

  // ── Org — Estrutura ──
  'org:read': {
    resource: 'org',
    action: 'read',
    category: 'org',
    description: 'View entities, departments, cost centers, segments',
    displayGroupKey: 'org',
    displayItemKey: 'read',
  },
  'org:manage': {
    resource: 'org',
    action: 'manage',
    category: 'org',
    description: 'Manage organizational structure',
    displayGroupKey: 'org',
    displayItemKey: 'manage',
  },

  // ── Workflow — Aprovações ──
  'workflow:read': {
    resource: 'workflow',
    action: 'read',
    category: 'workflow',
    description: 'View approval flows and rules',
    displayGroupKey: 'workflow',
    displayItemKey: 'read',
  },
  'workflow:manage': {
    resource: 'workflow',
    action: 'manage',
    category: 'workflow',
    description: 'Manage approval flows and rules',
    displayGroupKey: 'workflow',
    displayItemKey: 'manage',
  },

  // ── Integration — Integrações ──
  'integration:read': {
    resource: 'integration',
    action: 'read',
    category: 'integration',
    description: 'View integrations and dimensions',
    displayGroupKey: 'integration',
    displayItemKey: 'read',
  },
  'integration:manage': {
    resource: 'integration',
    action: 'manage',
    category: 'integration',
    description: 'Manage integrations and sync',
    displayGroupKey: 'integration',
    displayItemKey: 'manage',
  },

  // ── Report — Relatórios ──
  'report:read': {
    resource: 'report',
    action: 'read',
    category: 'report',
    description: 'View all reports (P&L, Balance Sheet, Trial Balance, Ledger)',
    displayGroupKey: 'report',
    displayItemKey: 'read',
  },
  'report:cash-flow:manage': {
    resource: 'report.cash-flow',
    action: 'manage',
    category: 'report',
    description: 'Create, edit, and delete manual cash-flow forecast items',
    displayGroupKey: 'report.cash-flow',
    displayItemKey: 'manage',
  },

  // ── Identity — Usuários & Acesso ──
  'identity:user:read': {
    resource: 'identity.user',
    action: 'read',
    category: 'identity',
    description: 'View users',
    displayGroupKey: 'identity.user',
    displayItemKey: 'read',
  },
  'identity:user:create': {
    resource: 'identity.user',
    action: 'create',
    category: 'identity',
    description: 'Create/invite users',
    displayGroupKey: 'identity.user',
    displayItemKey: 'create',
  },
  'identity:user:update': {
    resource: 'identity.user',
    action: 'update',
    category: 'identity',
    description: 'Edit users',
    displayGroupKey: 'identity.user',
    displayItemKey: 'update',
  },
  'identity:user:delete': {
    resource: 'identity.user',
    action: 'delete',
    category: 'identity',
    description: 'Remove users',
    displayGroupKey: 'identity.user',
    displayItemKey: 'delete',
  },
  'identity:role:read': {
    resource: 'identity.role',
    action: 'read',
    category: 'identity',
    description: 'View roles',
    displayGroupKey: 'identity.role',
    displayItemKey: 'read',
  },
  'identity:role:create': {
    resource: 'identity.role',
    action: 'create',
    category: 'identity',
    description: 'Create roles',
    displayGroupKey: 'identity.role',
    displayItemKey: 'create',
  },
  'identity:role:update': {
    resource: 'identity.role',
    action: 'update',
    category: 'identity',
    description: 'Edit roles and permissions',
    displayGroupKey: 'identity.role',
    displayItemKey: 'update',
  },
  'identity:permission:read': {
    resource: 'identity.permission',
    action: 'read',
    category: 'identity',
    description: 'View permission registry',
    displayGroupKey: 'identity.permission',
    displayItemKey: 'read',
  },
  'identity:tenant:read': {
    resource: 'identity.tenant',
    action: 'read',
    category: 'identity',
    description: 'View tenant info',
    displayGroupKey: 'identity.tenant',
    displayItemKey: 'read',
  },
  'identity:audit-log:read': {
    resource: 'identity.audit-log',
    action: 'read',
    category: 'identity',
    description: 'View audit trail',
    displayGroupKey: 'identity.audit-log',
    displayItemKey: 'read',
  },

  // ── Settings — Configurações ──
  'settings:read': {
    resource: 'settings',
    action: 'read',
    category: 'settings',
    description: 'View all settings (general, fiscal, themes, preferences)',
    displayGroupKey: 'settings',
    displayItemKey: 'read',
  },
  'settings:manage': {
    resource: 'settings',
    action: 'manage',
    category: 'settings',
    description: 'Manage all settings',
    displayGroupKey: 'settings',
    displayItemKey: 'manage',
  },

  // ── Fiscal obligations — Obrigações fiscais ──
  'fiscal:obligations:read': {
    resource: 'fiscal.obligations',
    action: 'read',
    category: 'fiscal',
    description: 'View the fiscal obligations catalog and each entity applicability decisions',
    displayGroupKey: 'fiscal.obligations',
    displayItemKey: 'read',
  },
  'fiscal:obligations:manage': {
    resource: 'fiscal.obligations',
    action: 'manage',
    category: 'fiscal',
    description:
      'Decide the fiscal obligations, CBS/IBS treatment and establishment profile of each Taxpayer and establishment',
    displayGroupKey: 'fiscal.obligations',
    displayItemKey: 'manage',
  },
  'fiscal:obligations:evidence': {
    resource: 'fiscal.obligations',
    action: 'evidence',
    category: 'fiscal',
    description:
      'Move fiscal obligation occurrences forward and record their evidence, payment and rectification',
    displayGroupKey: 'fiscal.obligations',
    displayItemKey: 'evidence',
  },

  // ── Dashboard ──
  'dashboard:read': {
    resource: 'dashboard',
    action: 'read',
    category: 'dashboard',
    description: 'View dashboard',
    displayGroupKey: 'dashboard',
    displayItemKey: 'read',
  },
} as const satisfies Record<string, PermissionDefinition>;

/**
 * Type-safe permission key. Adding to PERMISSIONS automatically extends this type.
 */
export type PermissionKey = keyof typeof PERMISSIONS;

/**
 * All permission keys as a runtime array. Useful for iteration and validation.
 */
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

// ── Reimbursement Access Contracts ───────────────────────────────
//
// Three aligned surfaces share REIMBURSEMENT_DETAIL_READ_ROUTE_PERMISSIONS:
// - FE: RoutePermissionGate + resolveRoutePermission('/ap/reimbursement-requests/:id')
// - API guard: @RequirePermission(...REIMBURSEMENT_DETAIL_READ_ROUTE_PERMISSIONS) on findOne/findByBill
// - API scope: ReimbursementAccessService.resolveReadScope() → LEGACY_POINT for legacy read;
//   combineWithPointReadWhere() applies tenant+id/bill filters only (no list scope).
// - Detail attachments: canLoadReimbursementDetailDocuments(); API verifies reimbursement linkage.
//
// List/browse surfaces use REIMBURSEMENT_READ_ROUTE_PERMISSIONS (scoped reads only).

/** @deprecated Prefer explicit scoped read permissions for request rows. */
export const REIMBURSEMENT_LEGACY_READ_PERMISSION =
  'ap:reimbursement:read' as const satisfies PermissionKey;

export const REIMBURSEMENT_READ_PERMISSIONS = [
  'ap:reimbursement:all:read',
  'ap:reimbursement:managed:read',
  'ap:reimbursement:own:read',
] as const satisfies readonly PermissionKey[];

export const REIMBURSEMENT_READ_ROUTE_PERMISSIONS = [
  ...REIMBURSEMENT_READ_PERMISSIONS,
  'ap:reimbursement:manage',
] as const satisfies readonly PermissionKey[];

/** Detail page and point lookups — includes legacy finance read for read-only viewing. */
export const REIMBURSEMENT_DETAIL_READ_ROUTE_PERMISSIONS = [
  REIMBURSEMENT_LEGACY_READ_PERMISSION,
  ...REIMBURSEMENT_READ_ROUTE_PERMISSIONS,
] as const satisfies readonly PermissionKey[];

/** True when the caller may open detail routes or point-read reimbursement APIs. */
export function hasReimbursementDetailReadAccess(permissions: readonly string[]): boolean {
  return (
    permissions.includes('*') ||
    REIMBURSEMENT_DETAIL_READ_ROUTE_PERMISSIONS.some((permission) =>
      permissions.includes(permission),
    )
  );
}

/** True when the caller may list or browse reimbursement requests (scoped reads only). */
export function hasReimbursementListReadAccess(permissions: readonly string[]): boolean {
  return (
    permissions.includes('*') ||
    REIMBURSEMENT_READ_ROUTE_PERMISSIONS.some((permission) => permissions.includes(permission))
  );
}

/** Org structure labels on the reimbursement detail surface (departments, cost centers, segments). */
export function hasReimbursementDetailOrgHydrationAccess(permissions: readonly string[]): boolean {
  return permissions.includes('*') || permissions.includes('org:read');
}

/** Full AP document inbox/list access. */
export function hasApDocumentReadAccess(permissions: readonly string[]): boolean {
  return permissions.includes('*') || permissions.includes('ap:document:read');
}

/**
 * Attachments on reimbursement detail — either inbox document read or detail reimbursement read
 * (API enforces reimbursement linkage for the latter).
 */
export function canLoadReimbursementDetailDocuments(permissions: readonly string[]): boolean {
  return hasApDocumentReadAccess(permissions) || hasReimbursementDetailReadAccess(permissions);
}

// ── Route → Permission Map ──────────────────────────────────────

/**
 * Maps frontend routes to required permissions.
 *
 * Used by:
 * - `<PermissionRoute>` component for route-level access control
 * - Sidebar filtering (nav items reference these same permissions)
 *
 * Routes without an entry require authentication only (no specific permission).
 * A route can require a single permission or any of multiple permissions.
 */
export const ROUTE_PERMISSIONS: Partial<Record<string, PermissionKey | readonly PermissionKey[]>> =
  {
    // Laboratório, the home page
    '/lab': 'dashboard:read',
    // The fleet run page (ALC-4668, D17): runs are tenant-wide and carry model cost, so tenant
    // admins only, like `Run now` on TR02.
    '/lab/agent-runs': 'settings:manage',
    '/identity/mcp-agents/catalog': 'settings:read',
    // AI-assistant write approvals (ALC-3889). `dashboard:read` mirrors the API
    // floor; approving additionally requires the target tool's own permission,
    // which only the server can resolve per row.
    '/assistente-ia/confirmacoes': 'dashboard:read',
    '/assistente-ia/confirmacoes/:confirmationId': 'dashboard:read',
    '/manager-dashboard': [
      'ap:reimbursement:all:read',
      'ap:reimbursement:managed:read',
      'ap:reimbursement:own:read',
      'ap:reimbursement:manage',
      'ap:contract-review:read',
      'ap:contract-review:risk-approve',
      'ap:contract-review:legal-approve',
      'op:credit-card:all:read',
      'op:credit-card:managed:read',
    ],

    // GL & Financial
    '/chart-of-accounts': 'gl:read',
    '/gl/ledger': 'gl:read',
    '/gl/origin-link': 'gl:origin-link:read',
    '/gl/manual-entries': 'gl:read',
    '/gl/manual-entries/new': 'gl:journal:draft',
    '/gl/manual-entries/:entryId': 'gl:read',
    '/fiscal/tax-assessments': 'settings:read',
    '/fiscal/obligations': 'fiscal:obligations:read',
    '/fiscal/tax-assessments/:taxpayerId/:assessmentId': 'settings:read',
    '/ap/expense-accruals': 'ap:expense-accrual:read',
    '/ap/expense-accruals/review': 'ap:expense-accrual:confirm',
    '/ap/expense-accruals/propose': 'ap:expense-accrual:propose',
    '/reports/balance-sheet': 'report:read',
    '/reports/trial-balance': 'report:read',
    '/reports/cash-flow': 'report:read',
    '/reports/cash-flow/monthly': 'report:read',
    '/budget': 'budget:read',
    '/budget/:budgetId': 'budget:read',
    '/budget/:budgetId/spreadsheet': 'budget:read',

    // FP&A (web viewer lands in slice G; permission mapping frozen in S0)
    '/fpa': 'fpa:read',
    '/fpa/hiring-plans': 'fpa:read',
    '/fpa/modelagem': 'fpa:manage',
    '/fpa/templates/new': 'fpa:manage',
    '/fpa/templates/:templateId': 'fpa:read',
    '/fpa/forecast-rule-sets': 'fpa:read',

    // AR
    '/ar/invoices': 'ar:read',
    '/ar/emitted-nfs': 'ar:read',
    '/ar/debit-notes': 'ar:debit-note:read',
    '/ar/pre-billing': 'ar:read',
    '/ar/customers': 'party:read',
    '/ar/customers/new': 'party:manage',
    '/ar/receipts': 'ar:read',

    // Billing (under ar)
    '/billing/ar-forecast': 'ar:read',
    '/billing/services': 'ar:service:read',
    '/billing/services/new': 'ar:service:create',
    // Cost Services (ALC-4474). Same keys as the billing services they follow.
    '/billing/cost-services': 'ar:service:read',
    '/billing/cost-services/new': 'ar:service:create',
    // Cost provisions (ALC-4476): the screens read `GET billing/cost-entries`, which is `ar:service:read`.
    // Closing and healing are gated inside the pages with `gl:revenue-recognition:manage`.
    '/billing/cost-provisions': 'ar:service:read',
    '/billing/cost-provisions/aging': 'ar:service:read',
    '/billing/groups': 'ar:read',
    '/billing/groups/new': 'ar:manage',
    // Revenue cycles (ALC-5094): reading needs ar:read; creating and editing need ar:manage (D23).
    '/billing/revenue-cycles': 'ar:read',
    '/billing/revenue-cycles/new': 'ar:manage',
    '/billing/contract-templates': 'ar:contract:read',
    '/billing/contract-templates/new': 'ar:contract:manage',
    '/billing/contracts': 'ar:contract:read',
    '/billing/contracts/new': 'ar:contract:manage',
    '/billing/contracts/from-template': 'ar:contract:manage',
    '/billing/metrics': 'ar:contract:read',

    // HCM
    '/hcm/employees': 'hcm:read',
    '/hcm/collaborators': 'hcm:read',
    '/hcm/payroll-runs': 'hcm:read',

    // AP
    '/ap/bills': 'ap:bill:read',
    '/ap/bills/v2': 'ap:bill:read',
    '/ap/bills/legacy': 'ap:bill:read',
    '/ap/bills/import': 'ap:bill:create',
    '/ap/deferrals': 'ap:bill:read',
    '/ap/payments': 'ap:payment:read',
    '/ap/payments/needs-review': 'ap:payment:read',
    '/ap/dda-inbox': 'integration:read',
    '/ap/recurring-dashboard': 'ap:bill:read',
    '/ap/vendors': 'party:read',
    '/ap/vendors/new': 'party:manage',
    '/ap/vendors/:id': 'party:read',
    '/ap/procurement': ['ap:purchase-request:read', 'ap:purchase-order:read'],
    '/ap/procurement/purchase-requests': 'ap:purchase-request:read',
    '/ap/procurement/purchase-requests/new': 'ap:purchase-request:create',
    '/ap/procurement/purchase-requests/:purchaseRequestId/edit': 'ap:purchase-request:manage',
    '/ap/procurement/purchase-orders': 'ap:purchase-order:read',
    '/ap/procurement/purchase-orders/new': 'ap:purchase-order:create',
    '/ap/documents': 'ap:document:read',
    '/ap/received-nfs': 'ap:received-nf:read',
    '/ap/custom-fields': 'org:manage',

    // AP Policies → workflow
    '/ap/policies/approval-workflows': 'workflow:read',
    '/ap/policies/procurement-approval-workflows': 'workflow:read',
    '/ap/policies/procurement-approval-workflows/:id': 'workflow:manage',
    '/ap/policies/reimbursements': 'workflow:read',
    '/ap/policies/reimbursements/new': 'workflow:manage',
    '/ap/policies/ingestion': 'workflow:read',
    '/ap/policies/approvers': 'workflow:read',
    '/ap/policies/procurement-settings': 'settings:manage',
    '/ap/policies/approval-settings': 'workflow:manage',
    '/ap/policies/classification-rules': 'workflow:read',
    '/ap/policies/contract-reviews': 'ap:contract-review:read',
    '/ap/policies/contract-reviews/new': 'ap:contract-review:manage',
    '/ap/policies/contract-reviews/settings': 'ap:contract-review:read',

    // Operations
    '/op/bank-accounts': 'op:read',
    '/op/bank-accounts/new': 'op:manage',
    '/op/card-fast-track': 'op:read',
    '/op/credit-cards': 'op:read',
    '/op/credit-cards/new': 'op:manage',
    '/op/investments': 'op:read',
    '/op/investments/gl-pending': 'op:read',
    '/op/loans': 'op:read',

    // Reconciliation (under op)
    '/reconciliation/inbox': 'op:read',
    '/reconciliation/queue': 'op:read',
    '/reconciliation/review': 'op:read',

    // Closing (under gl)
    '/closing/checklist': ['gl:read', 'gl:closing:manage'],
    '/closing/custom-tasks': 'gl:closing:task:manage',
    '/closing/multicurrency': 'gl:read',
    '/closing/ai-postings': 'ap:bill:read',

    // Assets (ADR 0003 / ALC-1655). Reads are `gl:read` — asset balances are GL balance-sheet
    // data measured per book, and the same permission already opens the Razão. The review
    // queue reuses the AP bill rails instead, because a candidate is a statement about how one
    // bill line is classified: `ap:bill:read` to see the queue, `ap:bill:reclassify` to decide,
    // which is what the API guards those two endpoints with.
    '/assets/review-queue': 'ap:bill:read',
    '/assets/registry': 'gl:read',
    // Hand-registering a bem creates one and posts its capitalisation, so it needs a write key,
    // not the `gl:read` its own detail page falls back to through the `/assets/registry/:assetId`
    // rule below. It must be the *same* write key the API demands: `POST assets` is guarded with
    // `gl:closing:manage` (`asset-registry.controller.ts`). Gating the page on
    // `ap:bill:reclassify` instead — the review queue's key — let a custom role holding one and
    // not the other either open a form whose submit always 403s, or be refused a form whose
    // endpoint would have accepted it.
    '/assets/registry/new': 'gl:closing:manage',
    '/assets/leases': 'gl:read',
    // Creating a lease is a write, so it takes the write key rather than the list's `gl:read`,
    // and the same key `POST assets/lease-contracts` is guarded with (`lease.controller.ts`).
    // Exact entries are matched before the `/assets/leases/:id` regex below, so this does not
    // collide with the detail page.
    '/assets/leases/new': 'gl:closing:manage',
    // Intangíveis (ADR 0017 D1/D12, ALC-3491). `gl:read` for the same reason
    // `/assets/registry` takes it, and because it is what the page's own reads are guarded
    // with: `IntangibleProjectController` puts the project list, the detail and the gate
    // template on `gl:read`, and the registry tab reads `GET /assets`, also `gl:read`. The
    // writes on the page — create, gate, go-live, write-off — are `gl:manage` and are gated
    // inside it with `Can`, so a `gl:read` reader opens the page and sees no action they
    // cannot take. The third tab (Alocações) reads `hcm:read` and is hidden without it; it is
    // not part of the route key, because a reader who may see projects but no people must
    // still reach the page.
    '/assets/intangibles': 'gl:read',

    // Assets configuration (ALC-1655). Takes `gl:manage`, not the `gl:read` the registry pages
    // take and not the AP rails the review queue takes. The screen is entirely a write surface:
    // it sets the capitalisation floor, the useful life per book, and the rules that decide
    // whether an invoice becomes an asset — changing any of them changes how EVERY subsequent
    // invoice is classified. An AP analyst who may reclassify one bill must not be able to
    // move the floor for all of them. `gl:manage` is the GL-configuration key ("books,
    // calendars, periods, account mappings"), FixedAssetClassBookRule is per-Book
    // configuration, and `/settings/books`, `/settings/calendars` and `/settings/periods`
    // already sit on it. Guarding the route at the write key rather than the read one means
    // the page is never opened by someone who can only look — every section on it is editable.
    '/assets/rules': 'gl:manage',
    '/assets/onboarding': 'gl:manage',

    // Identity & Access
    '/identity/users': 'identity:user:read',
    '/identity/users/new': 'identity:user:create',
    '/identity/users/edit': 'identity:user:update',
    '/identity/invitations': 'identity:user:read',
    '/identity/roles': 'identity:role:read',
    '/identity/roles/new': 'identity:role:create',
    '/identity/roles/edit': 'identity:role:update',
    '/identity/tenants': 'identity:tenant:read',
    '/identity/audit-logs': 'identity:audit-log:read',

    // Settings
    '/settings/departments': 'org:read',
    '/settings/cost-centers': 'org:read',
    '/settings/entities': 'org:read',
    '/settings/entities/new': 'org:manage',
    '/settings/global-settings': 'settings:read',
    '/settings/managerial-accounts': 'gl:read',
    '/settings/departments-canvas': 'org:read',
    '/settings/themes': 'settings:read',
    '/settings/books': 'gl:manage',
    '/settings/books/new': 'gl:manage',
    '/settings/calendars': 'gl:manage',
    '/settings/calendars/new': 'gl:manage',
    '/settings/periods': 'gl:manage',
    '/settings/period-planning': 'gl:manage',
    '/settings/rollups': 'fpa:read',
    '/settings/integrations': 'integration:read',
    '/settings/connected-agents': 'settings:read',
    '/settings/account-mapping': 'settings:manage',
    '/settings/hub/accounting-fiscal/account-mapping': 'settings:manage',
    '/settings/hub/accounting-fiscal/statutory-treatment': 'settings:read',
    // ALC-4430: Revenue Tax (PIS/COFINS) rules; the same permission as its settings-hub entry.
    '/settings/hub/accounting-fiscal/revenue-tax-rules': 'settings:read',
    '/settings/hub/accounts-payable/approval-settings': 'settings:read',
    '/settings/hub/procurement/procurement-settings': 'settings:manage',
    '/settings/hub/integrations/integrations-catalog': 'integration:read',
    '/settings/hub/integrations/dimension-reconciliation': 'integration:read',
    '/settings/hub/integrations/connected-agents': 'settings:read',
    '/settings/hub/organization/managerial-chart-of-accounts': 'gl:read',
    '/settings/hub/accounting-fiscal/managerial-chart-of-accounts': 'gl:read',
    '/settings/hub/users-permissions/users': 'identity:user:read',
    '/settings/hub/users-permissions/invitations': 'identity:user:read',
    '/settings/hub/users-permissions/roles': 'identity:role:read',
    '/settings/hub/hcm/payroll-settings': 'hcm:read',
    '/settings/hub/hcm/payroll-gl-mapping': 'hcm:read',
    '/settings/preferences': 'settings:read',
    '/settings/account': 'settings:read',
  };

/**
 * Resolves the permission requirement for a frontend route pathname.
 *
 * Checks `ROUTE_PERMISSIONS` for an exact match first, then applies
 * parameterized rules for child routes.
 */
export function resolveRoutePermission(
  pathname: string,
): PermissionKey | readonly PermissionKey[] | undefined {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  const exact = ROUTE_PERMISSIONS[normalized];
  if (exact) {
    return exact;
  }

  if (/^\/gl\/manual-entries\/[^/]+$/.test(normalized)) {
    return 'gl:read';
  }

  // A cycle's page is read by ar:read; the form gates its edits with ar:manage (D23).
  if (/^\/billing\/revenue-cycles\/[^/]+$/.test(normalized)) {
    return ROUTE_PERMISSIONS['/billing/revenue-cycles'];
  }

  if (/^\/lab\/agent-runs\/[^/]+$/.test(normalized)) {
    return ROUTE_PERMISSIONS['/lab/agent-runs'];
  }

  if (/^\/op\/bank-accounts\/[^/]+\/edit$/.test(normalized)) {
    return 'op:manage';
  }
  if (/^\/op\/bank-accounts\/[^/]+$/.test(normalized)) {
    return 'op:read';
  }

  if (/^\/op\/credit-cards\/[^/]+\/edit$/.test(normalized)) {
    return 'op:manage';
  }
  if (/^\/op\/credit-cards\/[^/]+$/.test(normalized)) {
    return 'op:read';
  }

  if (/^\/op\/investments\/[^/]+$/.test(normalized)) {
    return 'op:read';
  }
  if (/^\/op\/loans\/[^/]+$/.test(normalized)) {
    return 'op:read';
  }

  if (/^\/ap\/reimbursement-requests\/[^/]+$/.test(normalized)) {
    return REIMBURSEMENT_DETAIL_READ_ROUTE_PERMISSIONS;
  }

  // Vendor detail (ALC-4557): same key as the vendor list. `/ap/vendors/new` resolves to its
  // exact entry above first.
  if (/^\/ap\/vendors\/[^/]+$/.test(normalized)) {
    return ROUTE_PERMISSIONS['/ap/vendors/:id'];
  }

  if (/^\/ar\/customers\/[^/]+\/edit$/.test(normalized)) {
    return 'party:manage';
  }
  if (/^\/ar\/customers\/[^/]+$/.test(normalized)) {
    return 'party:read';
  }
  if (/^\/hcm\/employees\/[^/]+$/.test(normalized)) {
    return 'hcm:read';
  }

  if (/^\/reconciliation\/review\/[^/]+$/.test(normalized)) {
    return 'op:read';
  }

  // Asset and lease detail pages (ALC-1680). Same key as their list, and the same key the
  // registry endpoints are guarded with.
  if (/^\/assets\/registry\/[^/]+$/.test(normalized)) {
    return 'gl:read';
  }
  // Editing a hand-registered draft (ALC-4701): the key `PUT assets/:assetId` is guarded with,
  // the same write key as registering one.
  if (/^\/assets\/registry\/[^/]+\/edit$/.test(normalized)) {
    return 'gl:closing:manage';
  }
  if (/^\/assets\/leases\/[^/]+$/.test(normalized)) {
    return 'gl:read';
  }
  // Intangible project detail (ALC-3491): same key as the list, and the key
  // `GET assets/intangible-projects/:id` is guarded with. Its writes are gated inside with `Can`.
  if (/^\/assets\/intangibles\/[^/]+$/.test(normalized)) {
    return 'gl:read';
  }

  if (/^\/billing\/services\/[^/]+\/edit$/.test(normalized)) {
    return 'ar:service:edit';
  }

  // Cost Service detail (ALC-4474): same key as its list and as `GET billing/cost-services/:id`.
  // Its writes are gated inside the page with `ar:service:edit`.
  if (/^\/billing\/cost-services\/[^/]+$/.test(normalized)) {
    return 'ar:service:read';
  }

  // Provision entry log (ALC-4476): same key as its list and as `GET billing/cost-entries/:id`.
  if (/^\/billing\/cost-provisions\/[^/]+$/.test(normalized)) {
    return 'ar:service:read';
  }

  if (/^\/billing\/contract-templates\/[^/]+\/edit$/.test(normalized)) {
    return 'ar:contract:manage';
  }

  if (/^\/billing\/contracts\/[^/]+\/edit$/.test(normalized)) {
    return 'ar:contract:manage';
  }
  if (/^\/billing\/contracts\/[^/]+$/.test(normalized)) {
    return 'ar:contract:read';
  }

  if (
    /^\/settings\/hub\/integrations\/[^/]+$/.test(normalized) &&
    normalized !== '/settings/hub/integrations/integrations-catalog' &&
    normalized !== '/settings/hub/integrations/dimension-reconciliation' &&
    normalized !== '/settings/hub/integrations/connected-agents'
  ) {
    return 'integration:read';
  }

  if (
    /^\/settings\/integrations\/[^/]+$/.test(normalized) &&
    normalized !== '/settings/integrations/dimensions'
  ) {
    return 'integration:read';
  }

  if (/^\/budget\/[^/]+\/spreadsheet$/.test(normalized)) {
    return 'budget:read';
  }
  if (/^\/budget\/[^/]+$/.test(normalized)) {
    return 'budget:read';
  }

  if (/^\/fpa\/templates\/[^/]+\/edit$/.test(normalized)) {
    return 'fpa:manage';
  }

  if (/^\/fpa\/templates\/[^/]+$/.test(normalized)) {
    return 'fpa:read';
  }

  if (/^\/ap\/procurement\/purchase-requests\/[^/]+\/edit$/.test(normalized)) {
    return 'ap:purchase-request:manage';
  }
  if (/^\/ap\/procurement\/purchase-requests\/[^/]+$/.test(normalized)) {
    return 'ap:purchase-request:read';
  }
  if (/^\/ap\/procurement\/purchase-orders\/[^/]+$/.test(normalized)) {
    return 'ap:purchase-order:read';
  }
  if (/^\/ap\/policies\/procurement-approval-workflows\/[^/]+$/.test(normalized)) {
    return 'workflow:manage';
  }

  if (/^\/ar\/debit-notes\/[^/]+$/.test(normalized)) {
    return 'ar:debit-note:read';
  }

  return undefined;
}
